/**
 * Copyright (c) HashiCorp, Inc.
 */

import Component from '@glimmer/component';
import { HdsBadge } from '@hashicorp/design-system-components/components';

import CutListItem from '../list-item.gts';
import CutListItemTemplate from './template.gts';
import CutMetadataHealthCheckBadgeSet from '../metadata/health-check-badge-set.gts';
import CutMetadataInServiceMesh from '../metadata/in-service-mesh.gts';
import CutMetadataExternalSource from '../metadata/external-source.gts';
import CutMetadataTags from '../metadata/tags.gts';
import CutTextWithIcon from '../text-with-icon.gts';

import type { CutServiceInstance } from '../list-item.gts';

export interface ServiceInstanceListItemSignature {
  Args: {
    // List item args;
    href?: string;
    isHrefExternal?: boolean;
    route?: string;
    isRouteExternal?: boolean;
    query?: Record<string, unknown> | undefined;
    replace?: boolean;
    model?: string;
    models?: string[] | unknown[];
    onClick?: () => void;

    // Service args
    service?: CutServiceInstance;
  };
}

export default class ServiceInstanceListItem extends Component<ServiceInstanceListItemSignature> {
  get isAllHealthy() {
    // @ts-expect-error: need to fix service metadata typing
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { healthCheck } = this.args.service?.metadata;
    const serviceHealthy = healthCheck.service
      ? !healthCheck.service.critical && !healthCheck.service.warning
      : true;
    const nodeHealthy = healthCheck.node
      ? !healthCheck.node.critical && !healthCheck.node.warning
      : true;
    return serviceHealthy && nodeHealthy;
  }

  <template>
    <CutListItem
      @href={{@href}}
      @isRouteExternal={{@isRouteExternal}}
      @route={{@route}}
      @model={{@model}}
      @models={{@models}}
      @isHrefExternal={{@isHrefExternal}}
      @query={{@query}}
      @replace={{@replace}}
      @onClick={{@onClick}}
      data-test-service-instance-list-item
      as |L|
    >
      <L.Content>
        <CutListItemTemplate as |T|>
          <T.Section as |SC|>
            <SC.Title data-test-service-name>{{@service.name}}</SC.Title>
            <SC.Metadata>
              {{#if this.isAllHealthy}}
                <HdsBadge
                  @color="success"
                  @icon="check"
                  @text="Healthy"
                  @size="small"
                  data-test-health-check-all-healthy
                />
              {{else}}
                {{!Service health check}}
                {{#if @service.metadata.healthCheck.service}}
                  <CutMetadataHealthCheckBadgeSet
                    @successCount={{@service.metadata.healthCheck.service.success}}
                    @warningCount={{@service.metadata.healthCheck.service.warning}}
                    @criticalCount={{@service.metadata.healthCheck.service.critical}}
                    @type="service"
                  />
                {{/if}}
                {{!Node health check}}
                {{#if @service.metadata.healthCheck.node}}
                  <CutMetadataHealthCheckBadgeSet
                    @successCount={{@service.metadata.healthCheck.node.success}}
                    @warningCount={{@service.metadata.healthCheck.node.warning}}
                    @criticalCount={{@service.metadata.healthCheck.node.critical}}
                    @type="node"
                  />
                {{/if}}
              {{/if}}

              <CutMetadataInServiceMesh
                @connectedWithProxy={{@service.metadata.connectedWithProxy}}
              />

              {{#if @service.metadata.externalSource}}
                <CutMetadataExternalSource
                  @externalSource={{@service.metadata.externalSource}}
                />
              {{/if}}

              {{#if @service.metadata.node}}
                <CutTextWithIcon
                  @icon="node"
                  @text={{@service.metadata.node}}
                  data-test-node-name
                />
              {{/if}}

              {{#if @service.metadata.servicePortAddress}}
                <CutTextWithIcon
                  @icon="globe"
                  @text={{@service.metadata.servicePortAddress}}
                  data-test-address
                />

              {{else if @service.metadata.serviceSocketPath}}
                <CutTextWithIcon
                  @icon="socket"
                  @text={{@service.metadata.serviceSocketPath}}
                  data-test-socket
                />
              {{/if}}

              {{#if @service.metadata.tags.length}}
                <CutMetadataTags @tags={{@service.metadata.tags}} />
              {{/if}}
            </SC.Metadata>
          </T.Section>
        </CutListItemTemplate>
      </L.Content>
    </CutListItem>
  </template>
}
